import { marked } from 'marked';
import { finalizeNewsletterHtmlForMailerLite } from '../../supabase/functions/_shared/newsletter-email-finalize.ts';

const DEFAULT_BASE_URL = 'https://www.milkywayplanner.com';

/**
 * Site tokens (tailwind.config.ts): gold #c9922a, gold-foreground #93681b,
 * body #3d4f63, navy #0f1923, font-body Inter, font-serif Playfair Display.
 * Email clients drop <style> and classes, so every value is inlined.
 */
const GOLD = '#c9922a';
const GOLD_TEXT = '#93681b';
const NAVY = '#0f1923';
const BODY = '#3d4f63';
const MUTED = '#5f748d';
const CAPTION = '#5f748d';
const LINE = '#e2e8f0';
const PAPER = '#f8fafc';
const CALLOUT_BG = '#fdf8ef';
const CALLOUT_BORDER = '#e8d7b0';
const FONT_BODY =
  "Inter, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif";
const FONT_SERIF = "'Playfair Display', Georgia, 'Times New Roman', serif";

const CONTENT_IMG_WIDTH = '552';
const IMG_STYLE =
  `width:${CONTENT_IMG_WIDTH}px; max-width:100%; height:auto; border-radius:8px; display:block; border:0;`;
const FIGURE_STYLE = 'margin: 24px 0; text-align: center;';
const FIGCAPTION_STYLE =
  `margin-top: 8px; font-size: 14px; font-style: italic; color: ${CAPTION}; text-align: center;`;
const TABLE_STYLE =
  'width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px;';
const TH_STYLE =
  `text-align: left; padding: 8px 12px; border-bottom: 2px solid #1e3a5f; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; color: ${CAPTION};`;
const TD_STYLE =
  'padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; color: #3d4f63;';
const UL_STYLE =
  'margin: 16px 0; padding-left: 24px; list-style-type: disc; list-style-position: outside;';
const OL_STYLE =
  'margin: 16px 0; padding-left: 24px; list-style-type: decimal; list-style-position: outside;';
const LI_STYLE = 'margin: 0 0 8px 0; line-height: 1.6;';

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DD', 'DIV', 'DL', 'DT',
  'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE',
  'SECTION', 'TABLE', 'UL',
]);

const LOGO_SRC =
  'https://www.milkywayplanner.com/lovable-uploads/48a0f3cd-b38c-4efd-956b-0555da8f4f45.png';

export interface NewsletterPostSource {
  title: string;
  slug: string;
  excerpt?: string | null;
  featured_image?: string | null;
  featured_image_alt?: string | null;
  content: string;
}

export interface NewsletterPayload {
  title: string;
  slug: string;
  excerpt: string;
  featured_image?: string;
  content_html: string;
  email_html: string;
}

export type CampaignHtmlResult =
  | { ok: true; html: string | null }
  | { ok: false; error: string };

/**
 * Choose the MailerLite / preview body. A prebuilt document from
 * buildNewsletterPayload is sent unchanged. Null means the caller should
 * fall back to its own wrapper.
 */
export function resolveNewsletterCampaignHtml(emailHtml: unknown): CampaignHtmlResult {
  if (emailHtml == null || emailHtml === '') return { ok: true, html: null };
  if (typeof emailHtml !== 'string') {
    return { ok: false, error: 'email_html must be a string' };
  }
  if (emailHtml.length > 500_000) {
    return { ok: false, error: 'email_html must be a string under 500KB' };
  }
  return { ok: true, html: emailHtml };
}

export function markdownToEmailHtml(
  markdown: string,
  baseUrl: string = DEFAULT_BASE_URL,
  pageUrl: string = baseUrl,
): string {
  if (!markdown) return '';
  const parsed = marked.parse(markdown, { async: false }) as string;
  return transformEmailFragment(parsed, baseUrl, pageUrl);
}

export function buildNewsletterPayload(
  post: NewsletterPostSource,
  baseUrl: string = DEFAULT_BASE_URL,
): NewsletterPayload {
  const base = baseUrl.replace(/\/$/, '');
  const pageUrl = `${base}/blog/${post.slug}`;
  const featuredImage = post.featured_image
    ? toEmailImageUrl(post.featured_image, base, pageUrl)
    : undefined;
  const contentHtml = markdownToEmailHtml(post.content, base, pageUrl);
  const emailHtml = finalizeNewsletterHtmlForMailerLite(
    renderNewsletterDocument({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      featuredImage,
      featuredImageAlt: post.featured_image_alt || post.title,
      contentHtml,
      baseUrl: base,
    }),
    base,
  );

  return {
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt || '',
    ...(featuredImage ? { featured_image: featuredImage } : {}),
    content_html: contentHtml,
    email_html: emailHtml,
  };
}

function renderNewsletterDocument(input: {
  title: string;
  slug: string;
  excerpt: string;
  featuredImage?: string;
  featuredImageAlt?: string;
  contentHtml: string;
  baseUrl: string;
}): string {
  const base = input.baseUrl.replace(/\/$/, '');
  const postUrl = `${base}/blog/${encodeURIComponent(input.slug)}`;
  const year = new Date().getFullYear();
  const title = escapeHtml(input.title);
  const excerpt = input.excerpt
    ? `<p style="margin:0 0 24px; font-family:${FONT_BODY}; font-size:16px; line-height:1.7; font-style:italic; color:${BODY};">${escapeHtml(input.excerpt)}</p>`
    : '';
  const preview = escapeHtml(input.excerpt || input.title);
  const featured = input.featuredImage
    ? `<img src="${escapeHtml(input.featuredImage)}" alt="${escapeHtml(input.featuredImageAlt || input.title)}" width="${CONTENT_IMG_WIDTH}" style="${IMG_STYLE} margin:0 0 24px;">`
    : '';
  const linkStyle = `color:${GOLD_TEXT}; text-decoration:underline; font-size:14px; font-family:${FONT_BODY};`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="color-scheme" content="light only">
<title>${title}</title>
</head>
<body style="margin:0; padding:0; background-color:${PAPER}; color:${BODY}; font-family:${FONT_BODY};">
<div style="display:none; max-height:0; overflow:hidden; mso-hide:all; font-size:1px; line-height:1px; color:${PAPER}; opacity:0;">${preview}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; background-color:${PAPER}; margin:0; padding:0;">
<tr>
<td align="center" style="padding:0;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%; max-width:600px; background-color:#ffffff;">
<tr>
<td align="center" style="padding:28px 20px 24px; background-color:#ffffff; border-bottom:3px solid ${GOLD};">
<a href="${base}" style="text-decoration:none;">
<img src="${LOGO_SRC}" alt="Milky Way Planner" width="220" style="width:220px; max-width:100%; height:auto; display:block; margin:0 auto; border:0;">
</a>
</td>
</tr>
<tr>
<td style="padding:32px 24px; font-family:${FONT_BODY}; font-size:16px; line-height:1.7; color:${BODY};">
${featured}
<h1 style="margin:0 0 16px; font-family:${FONT_SERIF}; font-size:28px; font-weight:700; line-height:1.25; color:${NAVY};">${title}</h1>
${excerpt}
${input.contentHtml}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; margin:28px 0 8px;">
<tr>
<td align="center" style="padding:0;">
<a href="${postUrl}" style="display:inline-block; background-color:${GOLD}; color:${NAVY}; text-decoration:none; padding:14px 28px; border-radius:4px; font-weight:600; font-size:16px; font-family:${FONT_BODY};">Read on Website</a>
</td>
</tr>
</table>
</td>
</tr>
<tr>
<td align="center" style="padding:32px 20px; background-color:${PAPER}; border-top:1px solid ${LINE};">
<img src="${LOGO_SRC}" alt="Milky Way Planner" width="120" style="width:120px; max-width:100%; height:auto; display:block; margin:0 auto 8px; border:0;">
<p style="margin:0 0 4px; font-family:${FONT_BODY}; font-size:14px; font-weight:600; color:${NAVY};">Milky Way Planner</p>
<p style="margin:0 0 16px; font-family:${FONT_BODY}; font-size:14px; color:${MUTED};">Your astrophotography planning companion</p>
<p style="margin:0 0 16px; font-family:${FONT_BODY}; font-size:14px; line-height:1.6;">
<a href="${base}" style="${linkStyle}">Website</a>
&nbsp;&nbsp;
<a href="${base}/blog" style="${linkStyle}">Blog</a>
&nbsp;&nbsp;
<a href="mailto:support@milkywayplanner.com" style="${linkStyle}">Support</a>
</p>
<p style="margin:0; font-family:${FONT_BODY}; font-size:12px; line-height:1.5; color:${MUTED};">&copy; ${year} Milky Way Planner. All rights reserved.</p>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;
}

function transformEmailFragment(html: string, baseUrl: string, pageUrl: string): string {
  const doc = new DOMParser().parseFromString(
    '<!DOCTYPE html><html><body><div id="__email_root__"></div></body></html>',
    'text/html',
  );
  const root = doc.getElementById('__email_root__');
  if (!root) return html;
  root.innerHTML = html;

  root.querySelectorAll('script, style, iframe').forEach((el) => el.remove());
  stripEventHandlers(root);
  unwrapPictures(root);
  classifyBlocks(root);
  unwrapBlockAnchors(root);
  root.querySelectorAll('svg').forEach((el) => el.remove());
  pruneEmpty(root);
  absolutizeUrls(root, baseUrl, pageUrl);
  unwrapRedundantImageAnchors(root);
  applyTypography(root);
  convertStacks(root);
  wrapOrphanCards(root);
  root.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
  root.querySelectorAll('[data-email-block], [data-email-readmore]').forEach((el) => {
    el.removeAttribute('data-email-block');
    el.removeAttribute('data-email-readmore');
  });

  return root.innerHTML;
}

function unwrapPictures(root: HTMLElement) {
  [...root.querySelectorAll('picture')].forEach((picture) => {
    const img = picture.querySelector('img');
    if (!img) {
      picture.remove();
      return;
    }
    preferNonWebpSource(img, picture);
    img.removeAttribute('srcset');
    picture.replaceWith(img);
  });
  [...root.querySelectorAll('img')].forEach((img) => {
    preferNonWebpSource(img);
    img.removeAttribute('srcset');
  });
}

function preferNonWebpSource(img: Element, picture?: Element) {
  const src = img.getAttribute('src') || '';
  if (src && !isWebp(src)) return;
  const fromSrcset = firstNonWebp(img.getAttribute('srcset'));
  const fromPicture = picture
    ? [...picture.querySelectorAll('source')]
        .map((source) => firstNonWebp(source.getAttribute('srcset')))
        .find((url): url is string => !!url)
    : undefined;
  const fallback = fromSrcset || fromPicture || jpegSibling(src);
  if (fallback) img.setAttribute('src', fallback);
}

function jpegSibling(src: string): string | null {
  if (!src || !isWebp(src)) return null;
  return src.replace(/\.webp(?=$|[?#])/i, '.jpg');
}

function firstNonWebp(srcset: string | null): string | null {
  if (!srcset) return null;
  for (const part of srcset.split(',')) {
    const url = part.trim().split(/\s+/)[0];
    if (url && !isWebp(url)) return url;
  }
  return null;
}

function isWebp(url: string): boolean {
  return /\.webp(?:$|[?#])/i.test(url);
}

function classifyBlocks(root: HTMLElement) {
  root.querySelectorAll('div, a').forEach((el) => {
    const cls = el.getAttribute('class') || '';
    if (/\bgrid\b/.test(cls)) {
      el.setAttribute('data-email-block', 'stack');
      return;
    }
    if (/bg-gold/.test(cls)) {
      el.setAttribute('data-email-block', 'callout');
      return;
    }
    if (/rounded-xl/.test(cls) && (/border/.test(cls) || /\bring-/.test(cls))) {
      el.setAttribute('data-email-block', 'card');
    }
  });

  [...root.querySelectorAll('[data-email-block="card"]')].forEach((el) => {
    const sections = [...el.children].filter(
      (child) => child.tagName === 'DIV' && child.querySelector('p'),
    );
    if (sections.length >= 2) {
      el.setAttribute('data-email-block', 'stack');
      sections.forEach((section) => {
        if (!section.getAttribute('data-email-block')) {
          section.setAttribute('data-email-block', 'card');
        }
      });
    }
  });
}

function unwrapBlockAnchors(root: HTMLElement) {
  const anchors = [...root.querySelectorAll('a')].reverse();
  for (const anchor of anchors) {
    if (![...anchor.children].some((child) => BLOCK_TAGS.has(child.tagName))) continue;
    const label = extractCtaLabel(anchor) || 'Read more';
    const container = anchor.ownerDocument.createElement('div');
    container.setAttribute('data-email-block', anchor.getAttribute('data-email-block') || 'card');
    const href = anchor.getAttribute('href') || '';
    for (const child of [...anchor.childNodes]) {
      if (child.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((child as Element).tagName)) {
        container.appendChild(child);
      }
    }
    const line = anchor.ownerDocument.createElement('p');
    line.setAttribute('data-email-readmore', 'true');
    const link = anchor.ownerDocument.createElement('a');
    if (href) link.setAttribute('href', href);
    link.textContent = label;
    line.appendChild(link);
    container.appendChild(line);
    anchor.replaceWith(container);
  }
}

function extractCtaLabel(anchor: Element): string | null {
  const spans = [...anchor.querySelectorAll('span')].reverse();
  for (const span of spans) {
    const cls = span.getAttribute('class') || '';
    if (!/\binline-flex\b/.test(cls)) continue;
    span.querySelectorAll('svg').forEach((svg) => svg.remove());
    const text = (span.textContent || '').replace(/\s+/g, ' ').trim();
    span.remove();
    if (text) return text;
  }
  return null;
}

function stripEventHandlers(root: HTMLElement) {
  root.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
    });
  });
}

function pruneEmpty(root: HTMLElement) {
  let changed = true;
  while (changed) {
    changed = false;
    [...root.querySelectorAll('div, span')].forEach((el) => {
      if (el.querySelector('img, a, table, picture')) return;
      if ((el.textContent || '').trim()) return;
      el.remove();
      changed = true;
    });
  }
}

function absolutizeUrls(root: HTMLElement, baseUrl: string, pageUrl: string) {
  root.querySelectorAll('[href]').forEach((el) => {
    const href = el.getAttribute('href');
    if (href) el.setAttribute('href', toAbsoluteUrl(href, baseUrl, pageUrl));
  });
  root.querySelectorAll('[src]').forEach((el) => {
    const src = el.getAttribute('src');
    if (src) el.setAttribute('src', toEmailImageUrl(src, baseUrl, pageUrl));
  });
}

/** MailerLite treats <a href="image.jpg"><img src="image.jpg"></a> as a broken linked-image block. */
function unwrapRedundantImageAnchors(root: HTMLElement) {
  [...root.querySelectorAll('a')].forEach((anchor) => {
    const children = [...anchor.children];
    if (children.length !== 1 || children[0].tagName !== 'IMG') return;
    const href = anchor.getAttribute('href') || '';
    const src = children[0].getAttribute('src') || '';
    if (!href || !src) return;
    const normalizedHref = href.replace(/\.webp(?=$|[?#])/i, '.jpg');
    const normalizedSrc = src.replace(/\.webp(?=$|[?#])/i, '.jpg');
    if (normalizedHref === normalizedSrc) {
      anchor.replaceWith(children[0]);
    }
  });
}

function applyTypography(root: HTMLElement) {
  root.querySelectorAll('h1, h2, h3, h4, p, a, strong, em, span, ul, ol, li, blockquote, figure, figcaption, img, table, th, td').forEach((el) => {
    if (el.tagName === 'IMG') {
      styleImage(el as HTMLImageElement);
      return;
    }
    if (el.tagName === 'P') {
      el.setAttribute('style', paragraphStyle(el as HTMLElement));
      return;
    }
    if (/^H[1-4]$/.test(el.tagName)) {
      if (!el.getAttribute('style')) el.setAttribute('style', headingStyle(el.tagName));
      return;
    }
    if (el.tagName === 'A') {
      if (!el.getAttribute('style')) el.setAttribute('style', linkStyle(el));
      return;
    }
    if (el.tagName === 'STRONG') {
      if (!el.getAttribute('style')) {
        el.setAttribute('style', `font-weight:600; color:${NAVY};`);
      }
      return;
    }
    if (el.tagName === 'EM') {
      if (!el.getAttribute('style')) el.setAttribute('style', 'font-style:italic;');
      return;
    }
    if (el.tagName === 'SPAN') {
      const cls = el.getAttribute('class') || '';
      if (/font-medium|font-semibold|font-bold/.test(cls) && !el.getAttribute('style')) {
        el.setAttribute('style', `font-weight:600; color:${NAVY};`);
      }
      return;
    }
    if (el.tagName === 'UL' && !el.getAttribute('style')) {
      el.setAttribute('style', UL_STYLE);
      return;
    }
    if (el.tagName === 'OL' && !el.getAttribute('style')) {
      el.setAttribute('style', OL_STYLE);
      return;
    }
    if (el.tagName === 'LI' && !el.getAttribute('style')) {
      el.setAttribute('style', LI_STYLE);
      return;
    }
    if (el.tagName === 'BLOCKQUOTE' && !el.getAttribute('style')) {
      el.setAttribute(
        'style',
        `margin:16px 0; padding:12px 16px; border-left:3px solid ${GOLD}; background-color:${PAPER}; font-style:italic; color:${MUTED}; font-family:${FONT_BODY};`,
      );
      return;
    }
    if (el.tagName === 'FIGURE') {
      const style = el.getAttribute('style') || '';
      if (!style || /min\s*\(|margin-inline|\bvw\b|aspect-ratio/i.test(style)) {
        el.setAttribute('style', FIGURE_STYLE);
      }
      return;
    }
    if (el.tagName === 'FIGCAPTION') {
      const style = el.getAttribute('style') || '';
      if (!style || /min\s*\(|margin-inline|\bvw\b|aspect-ratio/i.test(style)) {
        el.setAttribute('style', FIGCAPTION_STYLE);
      }
      return;
    }
    if (el.tagName === 'TABLE' && !el.getAttribute('style')) {
      el.setAttribute('style', TABLE_STYLE);
      el.setAttribute('cellpadding', '0');
      el.setAttribute('cellspacing', '0');
      el.setAttribute('border', '0');
      return;
    }
    if (el.tagName === 'TH' && !el.getAttribute('style')) {
      el.setAttribute('style', TH_STYLE);
      return;
    }
    if (el.tagName === 'TD' && !el.getAttribute('style')) {
      el.setAttribute('style', TD_STYLE);
    }
  });
}

function paragraphStyle(p: HTMLElement): string {
  const cls = p.getAttribute('class') || '';
  if (p.hasAttribute('data-email-readmore')) {
    return `margin:12px 0 0; font-family:${FONT_BODY}; font-size:14px; line-height:1.5; color:${BODY};`;
  }
  if (p.getAttribute('style') && !cls) return p.getAttribute('style') || '';
  if (/font-serif/.test(cls)) {
    const size = /text-2xl/.test(cls) ? '28px' : '18px';
    return `margin:0 0 8px; font-family:${FONT_SERIF}; font-size:${size}; font-weight:700; line-height:1.3; color:${NAVY};`;
  }
  if (/text-xs/.test(cls)) {
    return `margin:0 0 8px; font-family:${FONT_BODY}; font-size:12px; line-height:1.5; color:${CAPTION};`;
  }
  if (/text-sm/.test(cls)) {
    return `margin:0 0 8px; font-family:${FONT_BODY}; font-size:14px; line-height:1.6; color:${MUTED};`;
  }
  if (/text-foreground/.test(cls)) {
    return `margin:0 0 16px; font-family:${FONT_BODY}; font-size:16px; line-height:1.7; color:${NAVY};`;
  }
  return `margin:0 0 16px; font-family:${FONT_BODY}; font-size:16px; line-height:1.7; color:${BODY};`;
}

function headingStyle(tag: string): string {
  if (tag === 'H1') {
    return `margin:0 0 16px; font-family:${FONT_SERIF}; font-size:28px; font-weight:700; line-height:1.25; color:${NAVY};`;
  }
  if (tag === 'H2') {
    return `margin:28px 0 12px; font-family:${FONT_SERIF}; font-size:22px; font-weight:700; line-height:1.3; color:${NAVY};`;
  }
  if (tag === 'H3') {
    return `margin:20px 0 10px; font-family:${FONT_SERIF}; font-size:18px; font-weight:700; line-height:1.4; color:${NAVY};`;
  }
  return `margin:16px 0 8px; font-family:${FONT_SERIF}; font-size:16px; font-weight:700; line-height:1.4; color:${NAVY};`;
}

function linkStyle(el: Element): string {
  if (isImageLink(el)) return 'display:inline; text-decoration:none; border:0;';
  const readMore = el.parentElement?.hasAttribute('data-email-readmore');
  return readMore
    ? `color:${GOLD_TEXT}; text-decoration:underline; font-weight:600;`
    : `color:${GOLD_TEXT}; text-decoration:underline;`;
}

function isImageLink(el: Element): boolean {
  const elements = [...el.children];
  const text = [...el.childNodes]
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent || '')
    .join('')
    .trim();
  return !text && elements.length === 1 && elements[0].tagName === 'IMG';
}

function styleImage(img: HTMLImageElement) {
  img.removeAttribute('srcset');
  img.removeAttribute('height');
  img.removeAttribute('loading');
  img.setAttribute('width', CONTENT_IMG_WIDTH);
  img.setAttribute('style', IMG_STYLE);
}

function convertStacks(root: HTMLElement) {
  const stacks = [...root.querySelectorAll('[data-email-block="stack"]')].reverse();
  for (const stack of stacks) {
    const items = [...stack.children];
    if (items.length === 0) {
      stack.remove();
      continue;
    }
    const table = presentationTable(stack.ownerDocument, '24px 0');
    items.forEach((item, index) => {
      appendItem(table, item);
      if (index < items.length - 1) appendSpacer(table);
    });
    stack.replaceWith(table);
  }
}

function wrapOrphanCards(root: HTMLElement) {
  const orphans = [...root.querySelectorAll('[data-email-block="card"], [data-email-block="callout"]')].filter(
    (el) => !el.closest('table'),
  );
  for (const el of orphans) {
    const table = presentationTable(el.ownerDocument, '24px 0');
    el.replaceWith(table);
    appendItem(table, el);
  }
}

function appendItem(table: HTMLTableElement, item: Element) {
  const tr = table.ownerDocument.createElement('tr');
  const td = table.ownerDocument.createElement('td');
  td.setAttribute('style', boxStyle(item));
  td.appendChild(item);
  tr.appendChild(td);
  table.appendChild(tr);
}

function boxStyle(item: Element): string {
  const kind = item.getAttribute('data-email-block');
  if (kind !== 'card' && kind !== 'callout') {
    return `padding:0; font-family:${FONT_BODY};`;
  }
  const callout = kind === 'callout';
  return `padding:16px 18px; background-color:${callout ? CALLOUT_BG : '#ffffff'}; border:1px solid ${callout ? CALLOUT_BORDER : LINE}; border-left:3px solid ${GOLD}; font-family:${FONT_BODY};`;
}

function appendSpacer(table: HTMLTableElement) {
  const tr = table.ownerDocument.createElement('tr');
  const td = table.ownerDocument.createElement('td');
  td.setAttribute('height', '12');
  td.setAttribute('style', 'height:12px; font-size:0; line-height:0;');
  td.innerHTML = '&nbsp;';
  tr.appendChild(td);
  table.appendChild(tr);
}

function presentationTable(doc: Document, margin: string): HTMLTableElement {
  const table = doc.createElement('table');
  table.setAttribute('role', 'presentation');
  table.setAttribute('width', '100%');
  table.setAttribute('cellpadding', '0');
  table.setAttribute('cellspacing', '0');
  table.setAttribute('border', '0');
  table.setAttribute(
    'style',
    `width:100%; max-width:600px; border-collapse:collapse; margin:${margin};`,
  );
  return table;
}

function toAbsoluteUrl(value: string, baseUrl: string, pageUrl: string): string {
  const url = value.trim();
  if (!url) return url;
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  const base = baseUrl.replace(/\/$/, '');
  if (url.startsWith('//')) return `https:${url}`;
  if (url.startsWith('#')) return `${pageUrl}${url}`;
  if (url.startsWith('/')) return `${base}${url}`;
  return `${base}/${url}`;
}

/** Email clients need absolute JPEG/PNG; site assets are often webp-only on disk. */
function toEmailImageUrl(value: string, baseUrl: string, pageUrl: string): string {
  const absolute = toAbsoluteUrl(value, baseUrl, pageUrl);
  return toEmailSafeRasterUrl(absolute);
}

function toEmailSafeRasterUrl(url: string): string {
  if (!url || !isWebp(url)) return url;
  return jpegSibling(url) || url;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
